import { useState, useEffect, useMemo, useCallback } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import CompanyPaymentProviderService from "../../../services/CompanyPaymentProviderService";
import CurrencyService, {
  CurrencyListResponseItem
} from "../../../services/CurrencyService";
import { CompanyProviderConfig } from "../../../entities/company/PaymentProvider/CompanyProviderConfig";
import {
  ProviderCurrency,
  SetProviderCurrencyRequest
} from "../../../entities/company/PaymentProvider/ProviderCurrency";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import { ModalService } from "../../shared/bootstrap/modal/Modal";
import Dropdown from "../../shared/components/Dropdown";
import Switch from "react-switch";
import TooltipComponent from "../../shared/TooltipComponent";

interface ProviderCurrencyModalProps {
  provider: CompanyProviderConfig;
  onClose: () => void;
  onSaved: () => void;
}

const ProviderCurrencyModal = ({
  provider,
  onClose,
  onSaved
}: ProviderCurrencyModalProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providerCurrencies, setProviderCurrencies] = useState<
    ProviderCurrency[]
  >([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<
    CurrencyListResponseItem[]
  >([]);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);

  // Formulario para agregar nueva moneda
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string | null>(
    null
  );
  const [clientSymbol, setClientSymbol] = useState<string>("");
  const [exchangeRateEnabled, setExchangeRateEnabled] =
    useState<boolean>(false);

  // Función para validar el símbolo del cliente
  const validateClientSymbol = useCallback(
    (symbol: string): { isValid: boolean; error?: string } => {
      const trimmed = symbol.trim();
      if (trimmed.length === 0) {
        return {
          isValid: false,
          error: TranslationService.translate.PleaseCompleteAllFields
        };
      }
      if (trimmed.length > 10) {
        return {
          isValid: false,
          error: TranslationService.translate.ClientSymbolMaxLength
        };
      }
      if (!/^[A-Z]+$/.test(trimmed)) {
        return {
          isValid: false,
          error: TranslationService.translate.ClientSymbolOnlyUppercase
        };
      }
      return { isValid: true };
    },
    []
  );

  // Función para recargar las monedas del proveedor
  const reloadProviderCurrencies = useCallback(async () => {
    if (!provider.PaymentLinkProviderId) {
      return;
    }

    try {
      const currenciesResponse =
        await CompanyPaymentProviderService.getProviderCurrencies(
          provider.PaymentLinkProviderId
        );
      if (!(currenciesResponse instanceof Error)) {
        setProviderCurrencies(currenciesResponse.list);
      } else {
        ToastService.showToast(
          currenciesResponse.message ??
            TranslationService.translate.ErrorLoadingCurrencies,
          undefined,
          "danger"
        );
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? TranslationService.translate.ErrorLoadingCurrencies,
        undefined,
        "danger"
      );
    }
  }, [provider.PaymentLinkProviderId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!provider.PaymentLinkProviderId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const errors: string[] = [];

      try {
        // Cargar monedas configuradas del proveedor
        const currenciesResponse =
          await CompanyPaymentProviderService.getProviderCurrencies(
            provider.PaymentLinkProviderId
          );
        if (currenciesResponse instanceof Error) {
          errors.push(
            currenciesResponse.message ??
              TranslationService.translate.ErrorLoadingCurrencies
          );
        } else {
          setProviderCurrencies(currenciesResponse.list);
        }

        // Cargar monedas disponibles
        const availableCurrenciesResponse = await CurrencyService.getList();
        if (availableCurrenciesResponse instanceof Error) {
          errors.push("Error al cargar las monedas disponibles.");
        } else {
          setAvailableCurrencies(availableCurrenciesResponse.list);
        }

        // Mostrar errores si los hay
        if (errors.length > 0) {
          ToastService.showToast(errors.join(" "), undefined, "danger");
        }
      } catch (e: any) {
        ToastService.showToast(
          e?.message ?? TranslationService.translate.ErrorLoadingCurrencies,
          undefined,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [provider.PaymentLinkProviderId]);

  // Preparar items para el dropdown de monedas disponibles (memoizado)
  const currencyItems = useMemo(() => {
    return availableCurrencies.map((currency) => ({
      value: currency.CurrencyID.toString(),
      text: `${currency.Name} (${currency.Symbol})`
    }));
  }, [availableCurrencies]);

  // Filtrar monedas ya configuradas para no mostrarlas en el dropdown (memoizado)
  const availableCurrencyItems = useMemo(() => {
    return currencyItems.filter((item) => {
      return !providerCurrencies.some(
        (pc) => pc.CurrencyID.toString() === item.value
      );
    });
  }, [currencyItems, providerCurrencies]);

  const handleAddCurrency = async () => {
    if (!provider.PaymentLinkProviderId) {
      ToastService.showToast(
        TranslationService.translate.InvalidProviderId,
        undefined,
        "danger"
      );
      return;
    }

    if (!selectedCurrencyId) {
      ToastService.showToast(
        TranslationService.translate.PleaseCompleteAllFields,
        undefined,
        "warning"
      );
      return;
    }

    // Validar el símbolo del cliente
    const validation = validateClientSymbol(clientSymbol);
    if (!validation.isValid) {
      ToastService.showToast(
        validation.error ??
          TranslationService.translate.PleaseCompleteAllFields,
        undefined,
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const request: SetProviderCurrencyRequest = {
        paymentLinkProviderId: provider.PaymentLinkProviderId,
        currencyID: parseInt(selectedCurrencyId),
        clientSymbol: clientSymbol.trim().toUpperCase(),
        ExchangeRateEnabled: exchangeRateEnabled ? 1 : 0
      };

      const response =
        await CompanyPaymentProviderService.setProviderCurrency(request);

      if (!(response instanceof Error)) {
        ToastService.showToast(
          TranslationService.translate.CurrencyAddedSuccessfully,
          undefined,
          "success"
        );

        // Recargar la lista de monedas configuradas
        await reloadProviderCurrencies();

        // Limpiar formulario
        setSelectedCurrencyId(null);
        setClientSymbol("");
        setExchangeRateEnabled(false);
        // Volver al listado
        setShowAddForm(false);
        onSaved();
      } else {
        throw response;
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? TranslationService.translate.ErrorAddingCurrency,
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCurrency = (currency: ProviderCurrency) => {
    const deleteMessage =
      TranslationService.translate.ConfirmDeleteCurrency.replace(
        "{symbol}",
        currency.ClientSymbol
      );
    ModalService.showDefaultModal({
      title: TranslationService.translate.Delete,
      message: deleteMessage,
      acceptButtonLabel: TranslationService.translate.Delete,
      acceptButtonClassName: "btn btn-danger",
      onAcceptClick: () => confirmDeleteCurrency(currency)
    });
  };

  const handleUpdateExchangeRate = async (
    currency: ProviderCurrency,
    enabled: boolean
  ) => {
    if (!provider.PaymentLinkProviderId) {
      ToastService.showToast(
        TranslationService.translate.InvalidProviderId,
        undefined,
        "danger"
      );
      return;
    }

    try {
      setSaving(true);

      const request: SetProviderCurrencyRequest = {
        paymentLinkProviderId: provider.PaymentLinkProviderId,
        currencyID: currency.CurrencyID,
        clientSymbol: currency.ClientSymbol,
        ExchangeRateEnabled: enabled ? 1 : 0
      };

      const response =
        await CompanyPaymentProviderService.setProviderCurrency(request);

      if (!(response instanceof Error)) {
        ToastService.showToast(
          TranslationService.translate.CurrencyUpdatedSuccessfully,
          undefined,
          "success"
        );

        // Recargar la lista de monedas configuradas
        await reloadProviderCurrencies();
        onSaved();
      } else {
        throw response;
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? TranslationService.translate.ErrorUpdatingCurrency,
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteCurrency = async (currency: ProviderCurrency) => {
    try {
      setSaving(true);

      const result = await CompanyPaymentProviderService.deleteProviderCurrency(
        currency.PaymentLinkProviderCurrencyId
      );

      if (result instanceof Error) {
        ToastService.showToast(
          result.message ?? TranslationService.translate.ErrorDeletingCurrency,
          undefined,
          "danger"
        );
      } else {
        ToastService.showToast(
          TranslationService.translate.CurrencyDeletedSuccessfully,
          undefined,
          "success"
        );

        // Recargar la lista de monedas configuradas
        await reloadProviderCurrencies();
        onSaved();
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? TranslationService.translate.ErrorDeletingCurrency,
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAdd = () => {
    // Limpiar formulario
    setSelectedCurrencyId(null);
    setClientSymbol("");
    setExchangeRateEnabled(false);
    // Volver al listado
    setShowAddForm(false);
  };

  // Función helper para normalizar ExchangeRateEnabled a booleano
  const isExchangeRateEnabled = (
    value: number | boolean | undefined
  ): boolean => {
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === "boolean") {
      return value;
    }
    return value === 1;
  };

  // Obtener el nombre de la moneda por su ID
  const getCurrencyName = (currencyId: number): string => {
    const currency = availableCurrencies.find(
      (c) => c.CurrencyID === currencyId
    );
    return currency
      ? `${currency.Name} (${currency.Symbol})`
      : `ID: ${currencyId}`;
  };

  if (loading) {
    return (
      <div className="floatingBody p-4">
        <div className="text-center py-4">
          <i className="fas fa-spinner-third fa-spin third"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="floatingBody p-4">
      <div className="ps-3 d-flex h-100 flex-column">
        {/* Nombre del proveedor (read-only) */}
        <div className="form-group mb-3">
          <label className="form-label">
            {TranslationService.translate.Provider}
          </label>
          <div className="form-control-plaintext fw-bold">
            {provider.PaymentProviderName}
          </div>
        </div>

        {/* Lista de monedas configuradas */}
        {!showAddForm && (
          <div className="border-top pt-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                {TranslationService.translate.ConfiguredCurrencies}
              </h6>
              <button
                type="button"
                className="btn btn-link text-primary p-0"
                onClick={() => setShowAddForm(true)}
                style={{ textDecoration: "none", fontWeight: 400 }}
              >
                {TranslationService.translate.AddNewCurrency}
              </button>
            </div>
            {providerCurrencies.length === 0 ? (
              <div className="text-muted text-center py-3">
                {TranslationService.translate.NoCurrenciesConfigured}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>
                        {TranslationService.translate.CurrencyTableHeader}
                      </th>
                      <th>
                        {TranslationService.translate.ClientSymbolTableHeader}
                      </th>
                      <th>
                        {TranslationService.translate.ExchangeRateTableHeader}
                      </th>
                      <th className="text-end">
                        {TranslationService.translate.Actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerCurrencies.map((currency) => (
                      <tr key={currency.PaymentLinkProviderCurrencyId}>
                        <td>{getCurrencyName(currency.CurrencyID)}</td>
                        <td>{currency.ClientSymbol}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Switch
                              onColor="#080"
                              checked={isExchangeRateEnabled(
                                currency.ExchangeRateEnabled
                              )}
                              onChange={(checked) =>
                                handleUpdateExchangeRate(currency, checked)
                              }
                              height={24}
                              width={50}
                              uncheckedIcon={false}
                              disabled={saving}
                            />
                            <span className="ms-2 small text-muted">
                              {isExchangeRateEnabled(
                                currency.ExchangeRateEnabled
                              )
                                ? TranslationService.translate.Enabled
                                : TranslationService.translate.Disabled}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <TooltipComponent
                            title={TranslationService.translate.Delete}
                            placement="right"
                            className="align-middle"
                          >
                            <button
                              type="button"
                              className="btn btn-link p-0 border-0 text-danger"
                              onClick={() =>
                                !saving && handleDeleteCurrency(currency)
                              }
                              disabled={saving}
                              aria-label={TranslationService.translate.Delete}
                              title={TranslationService.translate.Delete}
                            >
                              <i
                                className="far fa-fw fa-trash"
                                style={{
                                  cursor: saving ? "not-allowed" : "pointer",
                                  opacity: saving ? 0.5 : 1
                                }}
                              />
                            </button>
                          </TooltipComponent>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Formulario para agregar nueva moneda */}
        {showAddForm && (
          <div className="border-top pt-3">
            <h6 className="mb-3">
              {TranslationService.translate.AddNewCurrency}
            </h6>

            <div className="form-group mb-3">
              <label className="form-label">
                {TranslationService.translate.CurrencyRequired}
              </label>
              <div className="mt-2">
                <Dropdown
                  items={availableCurrencyItems}
                  onChange={setSelectedCurrencyId}
                  value={selectedCurrencyId}
                  placeholder={TranslationService.translate?.Select}
                />
              </div>
            </div>

            <div className="form-group mb-3">
              <label className="form-label">
                {TranslationService.translate.ClientSymbol}
              </label>
              <input
                type="text"
                className="form-control"
                value={clientSymbol}
                onChange={(e) => {
                  // Convertir a mayúsculas automáticamente y limitar a 10 caracteres
                  const value = e.target.value.toUpperCase().slice(0, 10);
                  setClientSymbol(value);
                }}
                disabled={saving}
                placeholder={TranslationService.translate.ClientSymbolExample}
                maxLength={10}
              />
              <small className="form-text text-muted">
                {TranslationService.translate.ClientSymbolDescription}
              </small>
            </div>

            <div className="form-group mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label mb-0">
                  {TranslationService.translate.EnableExchangeRate}
                </label>
                <Switch
                  onColor="#080"
                  checked={exchangeRateEnabled}
                  onChange={setExchangeRateEnabled}
                  height={24}
                  width={50}
                  uncheckedIcon={false}
                  disabled={saving}
                />
              </div>
              <small className="form-text text-muted">
                {TranslationService.translate.ExchangeRateDescription}
              </small>
            </div>
          </div>
        )}
      </div>

      <FloatingPanelFooter>
        {!showAddForm ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            {TranslationService.translate.Close}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancelAdd}
              disabled={saving}
            >
              {TranslationService.translate.Cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddCurrency}
              disabled={saving || !selectedCurrencyId || !clientSymbol.trim()}
            >
              {saving
                ? TranslationService.translate.Saving
                : TranslationService.translate.AddCurrency}
            </button>
          </>
        )}
      </FloatingPanelFooter>
    </div>
  );
};

export default ProviderCurrencyModal;
